/* @flow */

import React from 'react';
import { connect } from 'react-redux';
import Immutable from 'immutable';
import config from '../config';
import PropTypes from '../util/prop_types';
import withLocationManager from '../shared_components/hoc_location_manager';
import { MapActionCreators } from '../stores/map_action_creators';
import { MapSelectors } from '../stores/map_selectors';
import { ViewSelectors } from '../stores/view_selectors';
import MapGL from './map_gl';
import MapSearchMarkers from './map_search_markers';
import MapPopup from './map_popup';
import MapActions from './map_actions';

const mapStateToProps = (state) => {
  return {
    viewport: MapSelectors.getViewport(state),
    interactiveSources: ViewSelectors.getInteractiveSources(state),
    attributions: ViewSelectors.getAttributions(state),
    maxBounds: ViewSelectors.getMaxBounds(state),
    mapStyle: ViewSelectors.getStyleUrl(state),
    defaultViewport: ViewSelectors.getDefaultViewport(state),
  }
}

const mapDispatchToProps = {
  setViewport: MapActionCreators.setViewport,
  setPopupFromClick: MapActionCreators.setPopupFromClick,
};

class Map extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      width: 500,
      height: 500,
    };
  }

  static propTypes = {
    replaceLocation: PropTypes.func.isRequired,
    viewport: PropTypes.immutable.map,
    interactiveSources: PropTypes.immutable.map.isRequired,
    attributions: PropTypes.immutable.list,
    maxBounds: PropTypes.immutable.list.isRequired,
    mapStyle: PropTypes.string,
    defaultViewport: PropTypes.immutable.map,
    setViewport: PropTypes.func.isRequired,
    setPopupFromClick: PropTypes.func.isRequired,
  }

  componentDidMount() {
    window.addEventListener('resize', this._resize);
    this._resize();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this._resize);
  }

  componentWillReceiveProps(newProps) {
    const newViewport = newProps.viewport;
    const oldViewport = this.props.viewport;

    if (newViewport.isEmpty()) {
      newProps.setViewport(newProps.defaultViewport);
    }

    if (oldViewport != newViewport && !oldViewport.isEmpty()) {
      const didZoomUpdate = !oldViewport.get('zoom').equals(newViewport.get('zoom'));
      const didCenterUpdate = !oldViewport.get('center').equals(newViewport.get('center'));

      if (didZoomUpdate || didCenterUpdate) {
        const lat = newProps.viewport.getIn(['center', 1]).toFixed(7);
        const lng = newProps.viewport.getIn(['center', 0]).toFixed(7);
        const zoom = newProps.viewport.getIn(['zoom', 0]).toFixed(1);

        newProps.replaceLocation({lat, lng, zoom});
      }
    }
  }

  _resize = () => {
    this.setState({
      width: window.innerWidth,
      height: window.innerHeight - 55
    });
  };

  _getInteractiveFeature = (features) => {
    const { interactiveSources } = this.props;

    for (var i = 0, len = features.length; i < len; i++) {
      var layer = features[i].layer;
      if (layer.source && interactiveSources.has(layer.source)) {
        features[i].dataset = interactiveSources.get(layer.source).toJS();
        return features[i];
      }
      else if (layer['source-layer'] && interactiveSources.has(layer['source-layer'])) {
        features[i].dataset = interactiveSources.get(layer['source-layer']).toJS();
        return features[i];
      }
    }
    return false;
  }

  onFeatureClick = (e) => {
    if (e.features) {
      const feature = this._getInteractiveFeature(e.features);

      if (feature !== false) {
        this.props.setPopupFromClick(e.lngLat.lng, e.lngLat.lat, feature);
      }
    }
  }

  //given an event return the curser that should be shown
  getHoverCursor = (e) => {
    if (e.features) {
      const feature = this._getInteractiveFeature(e.features);

      return (feature !== false) ? 'pointer' : '';
    }
    return '';
  }

  render(): ?React.Element<any> {
    const {viewport, mapStyle} = this.props;
    const attributions = (this.props.attributions.count() > 0)
      ? this.props.attributions.toJS() : config.MAP.defaultAttributions;
    const maxBounds = (this.props.maxBounds) ? this.props.maxBounds.toJS() : undefined;

    if (viewport.count() > 0 && mapStyle) {
      return (
        <MapGL
          {...viewport.toJS()}
          attributionControl={false}
          attributions={attributions}
          width={this.state.width}
          height={this.state.height}
          mapStyle={mapStyle}
          maxBounds={maxBounds}
          accessToken={config.MAPBOX_TOKEN}
          onViewportChange={this.props.setViewport}
          onFeatureClick={this.onFeatureClick}
          getHoverCursor={this.getHoverCursor} >
          <MapActions />
          <MapSearchMarkers />
          <MapPopup />
        </MapGL>
      );
    }
    else {
      return null;
    }
  }
}

export default withLocationManager(connect(mapStateToProps, mapDispatchToProps)(Map));
